import { useEffect, useState } from "react";
import { FiArrowLeft, FiMessageSquare } from "react-icons/fi";
import ConversationsFetcher from "../../database/ConversationsFetcher";
import ConversationTransformer, {
  Conversation,
  HubspotConversationResponse,
} from "../../models/Conversation";
import { RegisteredUser } from "../../types/generic";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  FilterTabs,
  LoadingState,
  PageHeading,
} from "../UI";
import EmailConversation, {
  conversationTitle,
  conversationDate,
} from "./EmailConversation";
import styles from "./styles.module.css";

type Filter = "All" | "Open" | "Closed";
const keyOf = (conversation: Conversation) =>
  `${conversation.contact}:${conversation.id}`;

export default function UserConversations({ user }: { user: RegisteredUser }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [version, setVersion] = useState(0);
  const [filter, setFilter] = useState<Filter>("All");
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    const contacts = [user.email, ...(user.alternativeEmails || [])];
    Promise.all(
      contacts.map(async (contact) => {
        const response =
          (await ConversationsFetcher.fetchConversationsGivenEmail(
            contact,
          )) as HubspotConversationResponse["conversations"];
        // Transform each thread separately, including histories containing only one message.
        return (["openConversations", "closedConversations"] as const).flatMap(
          (group) =>
            response[group].flatMap((thread) =>
              thread.length
                ? ConversationTransformer.transformConversations([
                    {
                      contact,
                      conversations: {
                        openConversations:
                          group === "openConversations" ? [thread] : [],
                        closedConversations:
                          group === "closedConversations" ? [thread] : [],
                      },
                    },
                  ])
                : [],
            ),
        );
      }),
    )
      .then((groups) => {
        if (active)
          setConversations(
            groups
              .flat()
              .sort(
                (a, b) =>
                  (b.messages[b.messages.length - 1]?.createdAt.getTime() ||
                    0) -
                  (a.messages[a.messages.length - 1]?.createdAt.getTime() || 0),
              ),
          );
      })
      .catch((error) => {
        console.error("Error loading conversations:", error);
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user, version]);

  const visible = conversations.filter(
    (conversation) =>
      filter === "All" ||
      conversation.conversationStatus === filter.toUpperCase(),
  );
  const current =
    visible.find((conversation) => keyOf(conversation) === selected) ||
    visible[0];
  const showHistory = selected !== null && !!current;
  const changeFilter = (value: Filter) => {
    setFilter(value);
    setSelected(null);
  };

  return (
    <div className={`${styles.page} ${showHistory ? styles.detailView : ""}`}>
      <div className={styles.pageHeading}>
        <PageHeading
          title="Conversations"
          count={`${conversations.length} thread${conversations.length === 1 ? "" : "s"}`}
        />
      </div>
      {!loading && !failed && !visible.length && (
        <FilterTabs
          options={(["All", "Open", "Closed"] as const).map((value) => ({
            value,
            label: value,
          }))}
          value={filter}
          onChange={changeFilter}
          label="Conversation status"
        />
      )}
      {loading ? (
        <LoadingState label="Loading conversations…" />
      ) : failed ? (
        <ErrorState
          message="Conversations could not be loaded."
          onRetry={() => setVersion((value) => value + 1)}
        />
      ) : !visible.length ? (
        <EmptyState
          title={
            conversations.length
              ? `No ${filter.toLowerCase()} conversations`
              : "No conversations yet"
          }
          artwork={<FiMessageSquare size={32} aria-hidden="true" />}
        />
      ) : (
        <div className={styles.layout}>
          <div className={styles.threads}>
            <FilterTabs
              options={(["All", "Open", "Closed"] as const).map((value) => ({
                value,
                label: value,
              }))}
              value={filter}
              onChange={changeFilter}
              label="Conversation status"
            />
            <section
              className={styles.threadList}
              aria-label="Conversation list"
            >
              {visible.map((conversation) => {
                const isSelected = current === conversation;
                const lastMessage =
                  conversation.messages[conversation.messages.length - 1];
                return (
                  <button
                    type="button"
                    key={keyOf(conversation)}
                    className={`${styles.thread} ${isSelected ? styles.selected : ""}`}
                    onClick={() => setSelected(keyOf(conversation))}
                    aria-pressed={isSelected}
                  >
                    <strong>{conversationTitle(conversation)}</strong>
                    <span className={styles.contact}>
                      {conversation.contact}
                    </span>
                    <span className={styles.threadFooter}>
                      <Badge
                        status={
                          conversation.conversationStatus === "OPEN"
                            ? "open"
                            : "closed"
                        }
                      >
                        {conversation.conversationStatus === "OPEN"
                          ? "Open"
                          : "Closed"}
                      </Badge>
                      <span>{conversationDate(lastMessage?.createdAt)}</span>
                    </span>
                  </button>
                );
              })}
            </section>
          </div>
          {current && (
            <section
              className={styles.history}
              aria-label="Conversation history"
            >
              <Button
                className={styles.back}
                variant="ghost"
                onClick={() => setSelected(null)}
              >
                <FiArrowLeft aria-hidden="true" />
                Conversations
              </Button>
              <EmailConversation conversation={current} />
            </section>
          )}
        </div>
      )}
    </div>
  );
}
